/**
 * SpreadsheetNoteList Component
 *
 * @component
 * @description This component displays a list of spreadsheet notes in an accordion-style container.
 * It allows adding new notes and managing the visibility of existing notes.
 * Note: Saving and deleting functionality is intentionally left unimplemented.
 *
 * @param {SpreadsheetNoteData[]} props.notes - An array of SpreadsheetNoteData objects representing the notes to be displayed.
 * @returns {JSX.Element} - A JSX element representing the SpreadsheetNoteList component.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { isNull } from 'lodash';
import uuid from 'react-uuid';
import WorkbookContext from 'components/ScalarSpreadsheet/utilities/WorkbookContext';
import useSpreadsheetNoteTabs
  from 'components/SpreadsheetNotes/SpreadsheetNoteConfig/UseSpreadsheetNoteTabs/useSpreadsheetNoteTabs';
import useTabsMenu from 'components/TabsNavigation/hooks/useTabsMenu';
import { useLayoutContext } from 'context/LayoutContext';
import { getPageTypes } from 'services/hooks/notes/utilities';
import { TabsNavigation } from '../TabsNavigation';
import { SpreadsheetNoteData, SpreadsheetNoteListProps } from './types';

const SpreadsheetNoteList = (props: SpreadsheetNoteListProps) => {
  const { viewOnlyUser } = useLayoutContext();
  const { notes, setNotes, isDisabled = false, pageType, pageTypeId, handleOnDeleteNote } = props;
  const actionsAreDisabled = isDisabled || viewOnlyUser;
  const [visibleNotes, setVisibleNotes] = useState<SpreadsheetNoteData[]>([]);

  const {
    handleMenuOpen,
    handleMenuClose,
    menuAnchorEl
  } = useTabsMenu()

  // Note: onAddNote functionality is intentionally left as a placeholder
  const handleOnAddNote = useCallback(() => {

    const pageTypes = getPageTypes(pageTypeId);
    const pageTypeProps = pageTypes[pageType as keyof typeof pageTypes];
    setNotes((prevNotes: SpreadsheetNoteData[] | null) => {
      const newNote: SpreadsheetNoteData = {
        temp_id: uuid(),
        is_deleted: false,
        content: {},
        isNew: true,
        ...pageTypeProps
      }
      if (prevNotes?.length) {
        return [...prevNotes, newNote];
      }
      return [newNote];
    })
    // Implementation will be handled separately
  }, [pageType, pageTypeId, setNotes]);

  const {tabs, workbookContextValue} = useSpreadsheetNoteTabs({
    notes: visibleNotes,
    actionsAreDisabled,
    handleOnDeleteNote,
    handleMenuClose,
    handleOnAddNote,
    isAttributeMapperEnabled: false,
    documentId: 0
  })

  useEffect(() => {
    if (!isNull(notes)) {
      const tempVisibleNotes = notes?.filter(note => !note.is_deleted);
      setVisibleNotes(tempVisibleNotes);
    }
  }, [notes]);

  return (
    <WorkbookContext.Provider value={workbookContextValue}>

      <TabsNavigation
        menuAnchorEl={menuAnchorEl}
        mountOnActive
        handleMenuClose={handleMenuClose}
        handleMenuOpen={handleMenuOpen}
        relativeDrawer
        tabs={tabs}
      />
    </WorkbookContext.Provider>
  );
};

export default SpreadsheetNoteList;